<template>
  <div id="personal_details">
    <group-card title="Contact Details" icon="perm_identity" :tooltipText="tooltipText">
      <div class="boxed-category">
        <md-subheader>
          Contact&nbsp;
          <span v-if="applicant.contact.id == null"> (Search) </span>
          <span v-else-if="applicant.contact.id < 0"> (new customer) </span>
          <span v-else>
            (linked existing customer) Important: changes to contact details apply
            to all of the customers applications.
          </span>
        </md-subheader>
        <div class="md-layout input-layout">
          <!-- First Name -->
          <validated-text-input
            class="md-layout-item md-small-size-100 md-size-20 veda-required"
            label="First Name"
            :prefixLabel="prefixLabel"
            v-model="applicant.contact.name.firstname"
            ref="firstName"
            required
            regex="^[A-Za-z '-]*$"
          >
          </validated-text-input>

          <!-- Middle Name -->
          <validated-text-input
            class="md-layout-item md-small-size-100 md-size-20"
            label="Middle Name"
            :prefixLabel="prefixLabel"
            v-model="applicant.contact.name.middlename"
            regex="^[A-Za-z '-]*$"
          >
          </validated-text-input>

          <!-- Last Name -->
          <validated-text-input
            class="md-layout-item md-small-size-100 md-size-20 veda-required"
            label="Last Name"
            :prefixLabel="prefixLabel"
            v-model="applicant.contact.name.lastname"
            required
            regex="^[A-Za-z '-]*$"
          >
          </validated-text-input>

          <!-- Gender -->
          <validated-autocomplete-input
            class="md-layout-item md-small-size-100 md-size-20 veda-required"
            label="Gender"
            :prefixLabel="prefixLabel"
            v-model="applicant.contact.gender"
            :options="options.gender"
            required
            force
          />

          <!-- Date Of Birth -->
          <validated-input
            type="BIRTH_DATE"
            class="md-layout-item md-small-size-100 md-size-20 veda-required date-input"
            label="Date of Birth"
            :prefixLabel="prefixLabel"
            v-model="applicant.contact.date_of_birth"
            required
            prefixYear="past"
            errormsg="Date of Birth is not valid."
          />

          <!-- Email Address -->
          <validated-text-input
              class="md-layout-item md-small-size-100 md-size-40 veda-required"
              label="Email"
              :prefixLabel="prefixLabel"
              v-model="applicant.contact.emails[0].email"
              melissaCheckType="email"
              required
          />

          <!-- Contact Phone - Mobile - Home - Work -->
          <span v-for="phone in applicant.contact.phones" class="md-layout-item">
            <validated-text-input
              class="md-small-size-100 md-size-20"
              :label="phone.type + ' Contact Number'"
              :prefixLabel="prefixLabel"
              prefix = "+61"
              v-model="phone.phone_number"
              :minLength=6
              :maxLength=15
              errormsg="Please enter a valid phone number, format can contain 6 to 15 digits"
              melissaCheckType="phone"
            />
          </span>
        </div>
      </div>

      <div v-if="search.results.length > 0 && applicant.contact.id == null">
        <md-subheader class="alert-customer-match">Possible Existing Customer Match Found</md-subheader>
        <div class="padding">
          <table class="data-table wide">
            <thead>
              <tr>
                <th><code>Name</code></th>
                <th><code>Phone</code></th>
                <th><code>Email</code></th>
                <th><code>DOB</code></th>
                <th><code></code></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="customer in search.results"
                @dblclick="verifyCustomer(customer)"
              >
                <td>{{ customer.name | name }}</td>
                <td>{{ customer.phone }}</td>
                <td>{{ customer.email }}</td>
                <td>{{ customer.date_of_birth }}</td>
                <td class="right">
                  <!-- Open button | icon -->
                  <md-button
                    class="md-icon-button md-primary md-simple"
                    @click="verifyCustomer(customer)"
                  >
                    <md-icon>launch</md-icon>
                    <md-tooltip md-direction="top">Select Customer</md-tooltip>
                  </md-button>
                </td>
              </tr>

              <tr>
                <td colspan="4"><a href="#" @click="selectCustomer(null)">Continue with new customer details?</a></td>
                <td class="right">
                  <md-button
                    class="md-icon-button md-primary md-simple"
                    @click="selectCustomer(null)"
                  >
                    <md-icon>add</md-icon>
                    <md-tooltip md-direction="top">Create Customer</md-tooltip>
                  </md-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="boxed-category">
        <md-subheader>Personal Details</md-subheader>
        <div class="md-layout input-layout">
          <!-- Citizen -->
          <validated-autocomplete-input
            class="md-layout-item md-small-size-100 md-size-20"
            label="Australian Citizen"
            :prefixLabel="prefixLabel"
            v-model="applicant.personal_details.residency"
            :options="options.personal_details_residency_status"
            required
            force
          />

          <!-- Marital Status -->
          <validated-autocomplete-input
            class="md-layout-item md-small-size-100 md-size-20"
            label="Marital Status"
            :prefixLabel="prefixLabel"
            v-model="applicant.personal_details.marital_status"
            :options="options.personal_details_marital_status_options"
            required
            force
          />

          <!-- Number of Dependants -->
          <validated-number-input
            class="md-layout-item md-small-size-100 md-size-20"
            type="POSITIVE_INTEGER"
            label="Number of Dependants (Under 18)"
            :prefixLabel="prefixLabel"
            v-model="applicant.personal_details.number_of_dependants"
            :minValue=0
            :maxValue=20
            errormsg='Number of Dependants must be less than or equal 20'
           />

           <span class="note-text md-layout-item md-small-size-100 md-size-40" v-if="index > 0">
            <md-icon class="orange-icon">warning</md-icon>&nbsp;Only list additional dependants to those listed for primary applicant
           </span>
        </div>

        <!-- Dependant Age -->
        <div class="md-layout input-layout" v-if="(applicant.personal_details.number_of_dependants > 0 && applicant.personal_details.number_of_dependants <= 20)">
          <validated-input
            class="md-layout-item md-small-size-100 md-size-15"
            v-for="(dependant_detail, index) in applicant.personal_details.dependants_details"
            :key="index"
            type="POSITIVE_INTEGER"
            :label="`Dependant ${index+1} age`"
            :prefixLabel="prefixLabel"
            v-model="dependant_detail.age"
          />
        </div>

        <!-- Identification -->
        <div class="md-layout input-layout">
          <!-- ID Type -->
          <validated-autocomplete-input
            class="md-layout-item md-small-size-100 md-size-15 veda-required"
            label="ID Type"
            :prefixLabel="prefixLabel"
            v-model="id_type[0]"
            :options="options.identification_type[0]"
            required
            force
          />

          <!-- License Type -->
          <validated-autocomplete-input
            class="md-layout-item md-small-size-100 md-size-15 veda-required"
            label="License Type"
            :prefixLabel="prefixLabel"
            v-if="!isPassportSelected"
            v-model="applicant.personal_details.identifications[0].license_type"
            :options="options.license_type"
            force
          />

          <!-- State of Issue -->
          <validated-autocomplete-input
            class="md-layout-item md-small-size-100 md-size-15 veda-required"
            label="State of Issue"
            :prefixLabel="prefixLabel"
            v-if="!isPassportSelected"
            v-model="applicant.personal_details.identifications[0].state_of_issue"
            :options="states"
            force
          />

          <!-- License Number / Document ID -->
          <validated-text-input
            class="md-layout-item md-small-size-100 md-size-15 veda-required"
            label="License Number"
            :prefixLabel="prefixLabel"
            v-model="applicant.personal_details.identifications[0].id_number"
            alpha-num
          >
          </validated-text-input>

          <!-- Card Number -->
          <validated-text-input
            class="md-layout-item md-small-size-100 md-size-15 veda-required"
            label="Card Number"
            :prefixLabel="prefixLabel"
            v-if="!isPassportSelected"
            v-model="applicant.personal_details.identifications[0].card_number"
            alpha-num
          >
          </validated-text-input>

          <!-- Expiry Date -->
          <validated-input
            type="EXPIRY_DATE"
            class="md-layout-item md-small-size-100 md-size-15 veda-required date-input"
            label="Expiry Date"
            :prefixLabel="prefixLabel"
            v-model="applicant.personal_details.identifications[0].expiry_date"
            prefixYear="future"
          />
        </div>

        <div class="md-layout input-layout mt-5">
          <!-- ID Type -->
          <validated-autocomplete-input
            class=" md-layout-item md-small-size-100"
            label="ID Type"
            :prefixLabel="prefixLabel"
            v-model="id_type[1]"
            :options="options.identification_type[1]"
            force
          />

          <!-- Country of Issue -->
          <validated-autocomplete-input
            class=" md-layout-item md-small-size-100"
            label="Country of Issue"
            :prefixLabel="prefixLabel"
            v-model="applicant.personal_details.identifications[1].country_of_issue"
            :options="countries_list"
            force
          >
          </validated-autocomplete-input>

          <!-- License Number / Document ID -->
          <validated-text-input
            class=" md-layout-item md-small-size-100"
            label="Document ID"
            :prefixLabel="prefixLabel"
            v-model="applicant.personal_details.identifications[1].id_number"
            alpha-num
          >
          </validated-text-input>

          <!-- Expiry Date -->
          <validated-input
            type="DATE"
            class=" md-layout-item md-small-size-100 date-input"
            label="Expiry Date"
            :prefixLabel="prefixLabel"
            v-model="applicant.personal_details.identifications[1].expiry_date"
            prefixYear="future"
          />

            <!-- ID Type -->
          <validated-autocomplete-input
            class=" md-layout-item md-small-size-100 border_left"
            label="ID Type"
            :prefixLabel="prefixLabel"
            v-model="id_type[2]"
            :options="options.identification_type[2]"
            force
          />

          <validated-text-input
            class=" md-layout-item md-small-size-100"
            label="Card Number"
            :prefixLabel="prefixLabel"
            v-if="!isPassportSelected"
            v-model="applicant.personal_details.identifications[2].card_number"
            alpha-num
          >
          </validated-text-input>

          <validated-text-input
            class=" md-layout-item md-small-size-100"
            label="Name On Card"
            :prefixLabel="prefixLabel"
            v-model="applicant.personal_details.identifications[2].name_on_card"
          >
          </validated-text-input>

          <!-- Expiry Date -->
          <validated-input
            type="DATE"
            class=" md-layout-item md-small-size-100 date-input"
            label="Expiry Date"
            :prefixLabel="prefixLabel"
            v-model="applicant.personal_details.identifications[2].expiry_date"
            prefixYear="future"
          />
        </div>
      </div>
    </group-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import filters from "@/components/utils/filters";
import utils from "@/components/utils/utils";
import ValidatedInputs from "@/components/ValidatedInput";
import GroupCard from "@/components/Cards/GroupCard";
import GroupCardRow from "@/components/Cards/GroupCardRow";
import moment from 'moment';
import AlertDialog from "../Dialogs/AlertDialog";

export default {
  components: {
    ...ValidatedInputs,
    GroupCard,
    GroupCardRow,
  },
  data() {
    return {
      search: {
        key: "id",
        query: "",
        status: "initial",
        results: [],
        queryer: null,
      },
      id_type:['License','Passport','Medicare'],
      tooltipText: '<li>Please confirm all contact details are correct and are as shown on drivers license</li>'+
                    '<li>If on a visa - Confirm which visa number client is on - Cannot work with any Temporary Partner,'+
                     'Holiday, Bridging or Student visas. Time left on visa will impact also as a 6-month buffer is required -'+
                     ' so be mind full we start at 12 month term for loan and $10000 do they earn enough to pay back in 12 months?</li>'+
                    '<li>Please note we cannot do dual applications for Pension clients or on a Personal Loan. Please ensure that a privacy consent form is sent to the second applicant. (must live at the same residential address)</li>',
    };
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    prefixLabel: {
      type: String,
      default: "",
    },
    index: {
      type: Number,
      default: 0
    }
  },
  computed: {
    // standard list of options from store
    ...mapGetters({
      options: "options",
    }),

    // internal reference to the v-model on this component, handles incoming and outgoing changes
    applicant: {
      get() {
        // Following if condition will work for existing application to stop the crash
        if (this.value.personal_details.identifications.length < 3) {
          this.value.personal_details.identifications.push({card_number:'' , name_on_card:'' ,expiry_date:'', type:'Medicare'});
        }
        return this.value;
      },
      set(applicant) {
        this.$emit("input", applicant);
      },
    },

    customer_query() {
      return {
        first_name: this.applicant.contact.name.firstname,
        middle_name: this.applicant.contact.name.middlename,
        last_name: this.applicant.contact.name.lastname,
        email: this.applicant.contact.emails[0].email,
        phone: this.applicant.contact.phones[0].phone_number,
      };
    },

    states() {
      let country = this.options.countries && this.options.countries[0] || null;
      return this.options.country_states[country];
    },

    countries_list() {
      return this.options.countries;
    },

    isPassportSelected() {
      return this.identity_type == "Passport";
    },

    identity_type(){
      return this.applicant.personal_details.identifications[0].type;
    },
    isLoanDetailsRequestedAmountTabKeyPressed() {
      return this.$store.state.is_loan_details_requested_amount_tab_key_pressed;
    },
  },
  watch: {
    customer_query(query) {
      if (this.applicant.contact.id === null) {
        this.onCustomerSearchUpdate(query);
      }
    },
    identity_type() {
      if (this.isPassportSelected) {
        this.applicant.personal_details.identifications[0].state_of_issue = null;
      }
    },
    isLoanDetailsRequestedAmountTabKeyPressed: {
      handler(isTabKeyPressed) {
        if (isTabKeyPressed) {
          this.focusPoint();
        }
      },
    },
    "applicant.personal_details.number_of_dependants"(newVal) {
      const diffLength = newVal - this.applicant.personal_details.dependants_details.length;
      if (diffLength < 0) {
        this.applicant.personal_details.dependants_details = this.applicant.personal_details.dependants_details.slice(0, diffLength);
      } else {
        for (let i = 0; i < diffLength; i++) {
          this.applicant.personal_details.dependants_details.push({"age": 0});
        }
      }
    }
  },
  methods: {
    // make all filters available as methods
    ...filters,
    focusPoint(){
      let input_contact = this.$refs.firstName.$el.children[0];
      input_contact.querySelector('input').focus();
      this.$store.dispatch('on_loan_details_requested_amount_tab_key_pressed',false);
    },
    onCustomerSearchUpdate() {
      if (this.search.queryer == null) {
        this.search.queryer = new utils.LiveQueryer(this.performCustomerSearch);
      }

      this.search.queryer.query(this.customer_query);
    },
    async performCustomerSearch(query) {
      try {
        let response = await axios.post("/api/v1/customer/search", query);
        this.search.results = response.data;
      } catch (error) {
        console.error(error);
        this.search.results = [];
      }
    },
    selectCustomer(customer) {
      if (customer) {
        this.applicant.contact.id = customer.id;
        this.applicant.contact.name.title = customer.name.title;
        this.applicant.contact.name.firstname = customer.name.firstname;
        this.applicant.contact.name.middlename = customer.name.middlename;
        this.applicant.contact.name.lastname = customer.name.lastname;
        this.applicant.contact.gender = customer.gender;
        this.applicant.contact.date_of_birth = moment(customer.date_of_birth).format('YYYY-MM-DD');
        this.applicant.contact.emails[0].email = customer.email;
        for (let i = 0; i < this.applicant.contact.phones.length; i++) {
          if (this.applicant.contact.phones[i].type == customer.phone_type) {
            this.applicant.contact.phones[i].phone_number = customer.phone;
          }
        }
      } else {
        this.applicant.contact.id = -1;
      }
    },

    async verifyCustomer(customer) {
     try {
        await axios.get("/api/v1/checkCustomerActiveStatus/"+customer.id);
        this.selectCustomer(customer);
      } catch (error) {
        this.$modal.show(AlertDialog, { title:"Contact Error", message:error.response.data });
      }
    },
  },
  filters: {
    // make all filters available as filters
    ...filters,
  },
  async beforeCreate() {
    const params = {
      types: [
        'gender',
        'personal_details_residency_status',
        'personal_details_marital_status_options',
        'identification_type',
        'license_type',
        'countries',
        'country_states',
      ],
    }
    await this.$store.dispatch('fetch_types', { params });
  },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/theme.scss";
@import '../../assets/scss/md-custom/_ausloans-colors.scss';
/* Alert color for possible customer match during new application  */
.alert-customer-match {
  color: red !important;
}
.border_left {
  border-left: 2px solid $ausloans-blue-light;
}
.note-text {
  align-self: center;
}
.orange-icon {
  color: $ausloans-orange !important;
}
</style>
